/**
 * CR-8706: highlighting for NCR Detail fields the server refused on Save.
 *
 * Same look as the RCA / CAR tabs' required-field errors: a red border on the
 * input and the message underneath it.
 */

/** Field name to the message naming it (e.g. "Reject Qty cannot be empty"). */
export type DetailFieldErrors = Record<string, string>;

const ERROR_CLASS = 'border-red-500 focus-visible:ring-red-500 focus-visible:border-red-500';

/**
 * Props that mark one input as refused: red border, aria-invalid and a link
 * to the message rendered by {@link FieldError}.
 *
 * @param errors - the refused fields
 * @param field - backend field name of this input
 * @param base - the input's own classes, kept ahead of the error border
 * @returns Props to spread onto the input or select
 */
export function errorProps(errors: DetailFieldErrors, field: string, base = '') {
  const refused = !!errors[field];
  return {
    'aria-invalid': refused || undefined,
    'aria-describedby': refused ? `${field}-error` : undefined,
    className: refused ? `${base} ${ERROR_CLASS}`.trim() : base,
  };
}

interface FieldErrorProps {
  errors: DetailFieldErrors;
  field: string;
}

/**
 * The refusal message under a refused input; nothing when the field is fine.
 *
 * @param errors - the refused fields
 * @param field - backend field name of the input above
 * @returns The message paragraph, or null
 */
export function FieldError({ errors, field }: FieldErrorProps) {
  if (!errors[field]) return null;
  return (
    <p id={`${field}-error`} role="alert" className="text-sm text-red-600 mt-1">
      {errors[field]}
    </p>
  );
}
